import { useEffect, type RefObject } from 'react';
import { gsap, nf, prefersReducedMotion } from './motion';

/**
 * Scroll reveal for every `[data-reveal]` inside `root` (adds `.is-in` once visible)
 * and count-up for `.cnt[data-v]` numbers. Re-run when the content language changes so
 * freshly mounted elements get observed too.
 */
export function useReveal(root: RefObject<HTMLElement>, deps: readonly unknown[]) {
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const reduce = prefersReducedMotion();
    const io = new IntersectionObserver(
      (entries) => {
        for (const en of entries) {
          if (!en.isIntersecting) continue;
          en.target.classList.add('is-in');
          io.unobserve(en.target);
          if (!reduce) countUp(en.target as HTMLElement);
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -6% 0px' },
    );
    el.querySelectorAll<HTMLElement>('[data-reveal]').forEach((n) => {
      if (!n.classList.contains('is-in')) io.observe(n);
    });
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

function countUp(scope: HTMLElement) {
  scope.querySelectorAll<HTMLElement>('.cnt').forEach((c) => {
    const v = Number(c.dataset['v']);
    const dec = Number(c.dataset['dec'] ?? 0);
    const o = { v: 0 };
    gsap.to(o, { v, duration: 1.6, ease: 'power3.out', onUpdate: () => (c.textContent = dec ? o.v.toFixed(dec) : nf.format(Math.round(o.v))) });
  });
}
